import React, { useEffect } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { ScenarioOption } from '@hooks/scenarios-simulator/ui/use-scenario-selector';
import { ScenarioBasePath, ScenarioUnitPath } from '@hooks/scenarios-simulator/types';
import { IPresetTypeEnum } from '@schemas/scenarios-simulator/types';
import { sanitizePresetPayloadByPreset } from '@hooks/scenarios-simulator/business/preset-payload-cleaner';
import { getClarkeAclDefaultPresetPayload } from '@utils/scenarios-simulator/getClarkeAclDefaultPresetPayload';
import { getCompetitorAclDefaultPresetPayload } from '@utils/scenarios-simulator/getCompetitorAclDefaultPresetPayload';

interface UnitScenarioContainerProps {
  unitNum: number;
  scenarioBasePath: ScenarioBasePath;
  children: React.ReactNode;
}

interface ScenarioSectionProps {
  when: ScenarioOption;
  children: React.ReactNode;
  unitNum: number;
}

const ScenarioContext = React.createContext<string | null>(null);

export const useScenarioContext = () => {
  const context = React.useContext(ScenarioContext);
  if (context === undefined) {
    throw new Error('useScenarioContext must be used within UnitScenarioContainer');
  }
  return context;
};

function Root({ unitNum, scenarioBasePath, children }: UnitScenarioContainerProps) {
  const { setValue, getValues } = useFormContext();
  const basePath: ScenarioUnitPath = `${scenarioBasePath}.scenarioPayload.${unitNum}`;

  const presetType = useWatch({
    name: `${basePath}.presetType`,
  }) as string | null | undefined;

  const auxScenarioValue = useWatch({
    name: `${basePath}.presetPayload.aux__useScenarioSelector`,
  });

  useEffect(() => {
    if (!auxScenarioValue) return;

    const mapping: Record<string, IPresetTypeEnum> = {
      CLARKE_ACL: 'CLARKE_ACL',
      COMPETITOR_ACL: 'COMPETITOR_ACL',
      ACR: 'ACR',
    };

    const mapped = mapping[auxScenarioValue];
    if (!mapped) return;

    if (presetType !== mapped) {
      setValue(`${basePath}.presetType`, mapped, {
        shouldDirty: true,
        shouldValidate: false,
      });

      const currentPresetPayload = getValues(`${basePath}.presetPayload`);

      // Apply BT→ACL tariff fix BEFORE sanitization so consumptionInfo is cleaned with the correct tariff
      const isSwitchingToAcl = mapped === 'CLARKE_ACL' || mapped === 'COMPETITOR_ACL';
      const originalTariff = currentPresetPayload?.tariffGroup;
      const isBtTariff =
        originalTariff === 'B3_STANDARD' ||
        originalTariff === 'B3_OPTING' ||
        Number(originalTariff) === 8 ||
        Number(originalTariff) === 9;
      const payloadToSanitize =
        isSwitchingToAcl && isBtTariff ? { ...currentPresetPayload, tariffGroup: 'A4_GREEN' } : currentPresetPayload;

      const commonFields = sanitizePresetPayloadByPreset(mapped, payloadToSanitize) as Record<string, any>;
      delete commonFields.aux__useManualPrices;
      Object.keys(commonFields).forEach((key) => {
        if (commonFields[key] === null || commonFields[key] === undefined) {
          delete commonFields[key];
        }
      });

      if (mapped === 'CLARKE_ACL') {
        const defaultPayload = getClarkeAclDefaultPresetPayload(unitNum, scenarioBasePath.includes('baseScenario'));
        setValue(
          `${basePath}.presetPayload`,
          {
            ...defaultPayload.presetPayload,
            ...commonFields,
          },
          {
            shouldDirty: true,
            shouldValidate: false,
          },
        );
        setValue(`${basePath}.unitPayload`, defaultPayload.unitPayload, {
          shouldDirty: true,
          shouldValidate: false,
        });
      } else if (mapped === 'COMPETITOR_ACL') {
        const defaultPayload = getCompetitorAclDefaultPresetPayload(unitNum);
        setValue(
          `${basePath}.presetPayload`,
          {
            ...defaultPayload.presetPayload,
            ...commonFields,
          },
          {
            shouldDirty: true,
            shouldValidate: false,
          },
        );
        setValue(`${basePath}.unitPayload`, defaultPayload.unitPayload, {
          shouldDirty: true,
          shouldValidate: false,
        });
      } else if (mapped === 'ACR') {
        setValue(`${basePath}.presetPayload`, commonFields as any, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    }
  }, [auxScenarioValue, presetType, basePath, setValue, getValues, unitNum, scenarioBasePath]);

  const getCurrentScenario = (): string | null => {
    if (presetType) return presetType;

    switch (auxScenarioValue) {
      case 'CLARKE_ACL':
        return 'CLARKE_ACL';
      case 'COMPETITOR_ACL':
        return 'COMPETITOR_ACL';
      case 'ACR':
        return 'ACR';
      default:
        return null;
    }
  };

  const currentScenario = getCurrentScenario();

  return <ScenarioContext.Provider value={currentScenario}>{children}</ScenarioContext.Provider>;
}

function Section({ when, children, className }: ScenarioSectionProps & { className?: string }) {
  const currentScenario = useScenarioContext();

  if (currentScenario !== when) {
    return null;
  }

  return <section className={className}>{children}</section>;
}

function Always({ children, className }: { children: React.ReactNode; className?: string }) {
  return <section className={className}>{children}</section>;
}

export const UnitScenarioContainer = {
  Root,
  Section,
  Always,
};
